/**
 * A line chart, drawn as SVG by hand.
 *
 * No charting library, for reasons that are specific rather than ideological.
 * The design system's rule is that every colour comes from tokens.css and every
 * status stays distinguishable by shape alone in greyscale — a library brings
 * its own palette and its own marks and fights both. And the bundle had just
 * been split to keep CodeMirror off the message log's cold path; adding 400 kB
 * of charting to the eager bundle would undo that for one screen.
 *
 * Lines rather than stacked areas, because of a constraint that is easy to miss
 * until it is drawn: the nine delivery statuses map to five colours. Bounced,
 * complained and failed are all --oc-danger. Stacked, they are one red mass
 * with invisible internal boundaries. As lines, each carries its own status
 * glyph at every point and the shape does the identifying — which is what the
 * shapes were designed for, at 8px, in greyscale.
 */

import { useEffect, useId, useRef, useState } from "react";

import { SHAPE } from "./Status";
import type { MessageStatus } from "../api/types";

export interface Series {
  /** Drives both the colour and the marker shape. */
  status: MessageStatus;
  label: string;
  points: number[];
  /**
   * Drawn dashed. Used for the MPP-excluded open count, which is the same
   * series measured a second way rather than a different thing.
   */
  dashed?: boolean;
}

interface LineChartProps {
  series: Series[];
  /** One label per point. Only a few are drawn; the rest would collide. */
  labels: string[];
  height?: number;
  /** Describes the whole chart to a screen reader. */
  ariaLabel: string;
}

/** Room for the axis labels, in user units. */
const PADDING = { top: 12, right: 12, bottom: 22, left: 44 };

/**
 * Fallback width, used for the first paint before the container is measured.
 *
 * The viewBox used to be pinned to this permanently while the SVG stretched to
 * whatever width the panel gave it, with preserveAspectRatio="none". At 1160px
 * that scaled the drawing 1.61x horizontally and 1x vertically — so a 9x9
 * marker rendered 9.7x6.0, and the ratio changed as the window resized.
 *
 * Which quietly broke the thing this chart is built on. Circles became
 * ellipses, squares became rectangles, diamonds squashed; the nine shapes that
 * carry identity in greyscale stopped being nine distinct shapes. Vertical
 * stroke segments were 1.61x heavier than horizontal ones, which is why the
 * lines looked uneven.
 */
const FALLBACK_WIDTH = 720;
const MARKER = 9;

/**
 * How many points may carry a marker before they stop being legible.
 *
 * Past this the glyphs overlap into a textured band and identity moves back to
 * colour alone — which is the thing this chart exists to avoid. Beyond it,
 * markers thin out to a subset and the legend carries the shape instead.
 */
const MAX_MARKERS = 24;

export function LineChart({ series, labels, height = 220, ariaLabel }: LineChartProps) {
  const titleId = useId();

  // Measure the container and draw in its own units, so one user unit is one
  // CSS pixel and nothing is scaled. That is what keeps the markers round.
  const frame = useRef<HTMLDivElement>(null);
  const [viewWidth, setViewWidth] = useState(FALLBACK_WIDTH);

  useEffect(() => {
    const el = frame.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      // Math.round, because a fractional viewBox against a fractional container
      // reintroduces a fraction of the distortion this exists to remove.
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      if (next > 0) setViewWidth(next);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const pointCount = labels.length;
  const drawable = series.filter((s) => s.points.length > 0);

  // A chart of nothing is a lie told with axes. Say so instead.
  const peak = Math.max(0, ...drawable.flatMap((s) => s.points));
  if (pointCount === 0 || peak === 0) {
    // The ref goes on this branch too. Without it, a chart that renders empty
    // first — a range with no events, then a reload that finds some — would
    // attach the ref only after the effect had already run with an empty deps
    // array, and stay pinned to the fallback width forever.
    return (
      <div className="oc-chart" ref={frame}>
        <div className="oc-chart__empty">Nothing happened in this period.</div>
        <Legend series={series} />
      </div>
    );
  }

  const plotWidth = viewWidth - PADDING.left - PADDING.right;
  const plotHeight = height - PADDING.top - PADDING.bottom;

  // Rounded up to something a human reads as a scale, so the top gridline is a
  // number rather than whatever the largest bucket happened to be.
  const ceiling = niceCeiling(peak);

  const x = (index: number) =>
    PADDING.left + (pointCount === 1 ? plotWidth / 2 : (index / (pointCount - 1)) * plotWidth);
  const y = (value: number) => PADDING.top + plotHeight - (value / ceiling) * plotHeight;

  const gridValues = [0, ceiling / 2, ceiling];
  const markerStep = Math.max(1, Math.ceil(pointCount / MAX_MARKERS));

  // A handful of x labels, evenly spaced. Every bucket labelled is unreadable
  // at any realistic width.
  const labelStep = Math.max(1, Math.ceil(pointCount / 6));

  return (
    <div className="oc-chart" ref={frame}>
      <svg
        className="oc-chart__svg"
        viewBox={`0 0 ${viewWidth} ${height}`}
        height={height}
        role="img"
        aria-labelledby={titleId}
      >
        <title id={titleId}>{ariaLabel}</title>

        {gridValues.map((value) => (
          <g key={value}>
            <line
              className="oc-chart__grid"
              x1={PADDING.left}
              x2={viewWidth - PADDING.right}
              y1={y(value)}
              y2={y(value)}
            />
            <text className="oc-chart__axis" x={0} y={y(value) + 4}>
              {formatCount(value)}
            </text>
          </g>
        ))}

        {labels.map((label, index) =>
          index % labelStep === 0 ? (
            <text
              key={label + index}
              className="oc-chart__axis"
              x={x(index)}
              y={height - 6}
              textAnchor={index === 0 ? "start" : "middle"}
            >
              {label}
            </text>
          ) : null,
        )}

        {drawable.map((s) => (
          <g key={s.label} className="oc-chart__series" data-status={s.status}>
            <polyline
              className={`oc-chart__line${s.dashed ? " oc-chart__line--dashed" : ""}`}
              points={s.points.map((value, index) => `${x(index)},${y(value)}`).join(" ")}
            />
            {/*
              The marker is the status shape itself, pulled from the same sprite
              the chips use. currentColor resolves from the group, so colour and
              shape can never disagree about which series this is.
            */}
            {s.points.map((value, index) =>
              index % markerStep === 0 ? (
                <use
                  key={index}
                  href={`#${SHAPE[s.status]}`}
                  x={x(index) - MARKER / 2}
                  y={y(value) - MARKER / 2}
                  width={MARKER}
                  height={MARKER}
                />
              ) : null,
            )}
          </g>
        ))}
      </svg>

      <Legend series={series} />
    </div>
  );
}

function Legend({ series }: { series: Series[] }) {
  return (
    <div className="oc-chart__legend">
      {series.map((s) => (
        <span key={s.label} className="oc-chart__legend-item">
          <svg
            className="oc-chart__legend-glyph oc-chart__series"
            data-status={s.status}
            aria-hidden="true"
            focusable="false"
          >
            <use href={`#${SHAPE[s.status]}`} />
          </svg>
          {s.label}
          {s.dashed && <span className="oc-text-muted">(dashed)</span>}
        </span>
      ))}
    </div>
  );
}

/**
 * Rounds a peak up to a readable axis maximum.
 *
 * 1, 2 or 5 times a power of ten — the scale people already read on every other
 * chart they have seen, and it makes the halfway gridline a round number too.
 */
function niceCeiling(peak: number): number {
  if (peak <= 1) return 1;

  const magnitude = 10 ** Math.floor(Math.log10(peak));
  for (const step of [1, 2, 5, 10]) {
    if (peak <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

/** Compact axis labels: 1.2k rather than 1200, which does not fit. */
export function formatCount(value: number): string {
  if (value < 1000) return String(Math.round(value));
  if (value < 1_000_000) return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)}k`;
  return `${(value / 1_000_000).toFixed(1)}M`;
}
